export const SPILL_INCHES = 105
export const RUNGS = [12, 24, 36, 48, 60, 72, 84, 96]
export const CAPACITY_BBL = 500

export type TankStyle = 'frac' | 'upright' | 'commander'

export const TANK_KINDS = [
  {
    id: '500-bbl-frac',
    label: '500 BBL Frac Tank',
    shortLabel: '500 Frac',
    style: 'frac' as TankStyle,
    capacityBbl: 500,
    spillInches: 105,
    blurb: 'Horizontal frac tanks. Spill at 8 ft 9 in.',
  },
  {
    id: '400-bbl-upright',
    label: '400 BBL Upright Tank',
    shortLabel: '400 Upright',
    style: 'upright' as TankStyle,
    capacityBbl: 400,
    spillInches: 240,
    blurb: '12 ft × 20 ft upright. 400 BBL at 20 ft (1.667 BBL/in).',
  },
  {
    id: 'sand-commander',
    label: 'Sand Commander',
    shortLabel: 'Commander',
    style: 'commander' as TankStyle,
    capacityBbl: 110,
    unitCapacityBbl: 110,
    spillInches: 72,
    weirInches: 48,
    bays: 4,
    blurb: 'Sand Commander. 110 BBL at 6 ft. Keep the last bay below 48 in — at 48 in all four lock and the whole tank fills as one.',
  },
] as const

export type TankKindId = (typeof TANK_KINDS)[number]['id']

export function tankKind(raw: unknown) {
  const id = String(raw ?? '')
  return TANK_KINDS.find((k) => k.id === id) || TANK_KINDS[0]
}

export function isWaterFluid(label?: unknown) {
  const k = String(label ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ')
  return ['fresh', 'water', 'fresh water', 'heavy', 'packer fluid', 'brine'].includes(k)
}

export const COMMANDER_FULL_INCHES = 72
export const COMMANDER_GLASS_INCHES = 69
export const COMMANDER_WEIR_INCHES = 48
export const COMMANDER_SPILL_INCHES = 72
export const COMMANDER_BAY_BBL = 27.5
export const COMMANDER_BBL_PER_INCH = 27.5 / 72
export const COMMANDER_LABELS = ['Hopper', 'Baffle 2', 'Baffle 3', 'Strap'] as const

export function commanderSpillFor(tank: any) {
  return commanderBayIndex(tank) >= 3 ? COMMANDER_FULL_INCHES : COMMANDER_WEIR_INCHES
}

export function spillInchesFor(tank: any) {
  const kind = tankKind(tank?.tankType ?? tank?.tank_type)
  if (kind.style === 'commander') return commanderSpillFor(tank)
  const fromTank = Number(tank?.maxHeightInches ?? tank?.max_height_inches ?? 0)
  if (fromTank > 0) return fromTank
  return kind.spillInches
}

export type WatchStatus = 'high' | 'haul' | 'empty' | 'inUse' | null

export const WATCH_FLAGS: { status: Exclude<WatchStatus, null>; label: string; pill: string; color: string }[] = [
  { status: 'high', label: 'High', pill: 'HIGH', color: '#FF6F7D' },
  { status: 'haul', label: 'Haul off', pill: 'HAUL OFF', color: '#E0A34E' },
  { status: 'empty', label: 'Empty', pill: 'EMPTY', color: '#43BDE2' },
  { status: 'inUse', label: 'In use', pill: 'IN USE', color: '#3DDC84' },
]

export function normalizeWatchStatus(raw: unknown): WatchStatus {
  const k = String(raw ?? '').trim().toLowerCase().replace(/[-\s]+/g, '_')
  if (k === 'high') return 'high'
  if (k === 'haul' || k === 'haul_off' || k === 'hauloff') return 'haul'
  if (k === 'empty') return 'empty'
  if (k === 'inuse' || k === 'in_use') return 'inUse'
  return null
}

export function watchMeta(status: WatchStatus) {
  return WATCH_FLAGS.find((f) => f.status === status) || null
}

export function bblFromInches(inches: number, capacity = CAPACITY_BBL, spill = SPILL_INCHES) {
  return capacity * (Math.max(0, inches) / spill)
}

export function inchesFromBbl(bbl: number, capacity = CAPACITY_BBL, spill = SPILL_INCHES) {
  return (Math.max(0, bbl) / Math.max(1, capacity)) * spill
}

function finalizeCuts(tank: any, cuts: { waterInches: number; oilInches: number; totalInches: number; capacityBbl: number; spillInches: number }) {
  const label = String(tank?.fluidLabel ?? tank?.fluid_label ?? tank?.fluid_type ?? '').trim().toLowerCase()
  const commander = tankKind(tank?.tankType ?? tank?.tank_type).style === 'commander'
  const treatWater = isWaterFluid(label) || (commander && label !== 'oil')
  if (treatWater && cuts.waterInches <= 0 && cuts.totalInches > 0) {
    return { ...cuts, waterInches: cuts.totalInches, oilInches: 0 }
  }
  return cuts
}

export function tankCuts(tank: any) {
  const cap = Number(tank?.capacityBbl ?? tank?.capacity_bbl ?? CAPACITY_BBL)
  const spill = spillInchesFor(tank)
  const water = Math.max(0, Number(tank?.waterInches ?? tank?.water_inches ?? tank?.current?.water_inches ?? 0) || 0)
  const oilColumnRaw = tank?.oilColumnInches ?? tank?.oil_column_inches ?? tank?.current?.oil_column_inches
  const oilColumn = oilColumnRaw == null || oilColumnRaw === '' ? null : Number(oilColumnRaw)
  const topRaw = tank?.totalInches ?? tank?.total_inches ?? tank?.current?.total_inches
  const top = topRaw == null || topRaw === '' ? null : Number(topRaw)

  if (oilColumn != null && Number.isFinite(oilColumn)) {
    const oilInches = Math.max(0, oilColumn)
    const totalInches = top != null && Number.isFinite(top) ? Math.max(water, top) : water + oilInches
    return finalizeCuts(tank, { waterInches: water, oilInches, totalInches, capacityBbl: cap, spillInches: spill })
  }

  if (top != null && Number.isFinite(top) && top > 0) {
    return finalizeCuts(tank, {
      waterInches: water,
      oilInches: Math.max(0, top - water),
      totalInches: Math.max(water, top),
      capacityBbl: cap,
      spillInches: spill,
    })
  }

  const oilBbl = Number(tank?.oilBbl ?? tank?.oil_bbl ?? tank?.current?.oil_bbl ?? 0)
  const waterBbl = Number(tank?.waterBbl ?? tank?.water_bbl ?? tank?.current?.water_bbl ?? 0)
  if (oilBbl > 0 || waterBbl > 0) {
    return finalizeCuts(tank, {
      waterInches: inchesFromBbl(waterBbl, cap, spill),
      oilInches: inchesFromBbl(oilBbl, cap, spill),
      totalInches: inchesFromBbl(oilBbl + waterBbl, cap, spill),
      capacityBbl: cap,
      spillInches: spill,
    })
  }

  // Legacy: oilInches was stored as top-of-fluid on older payloads.
  const legacy = Number(tank?.oilInches ?? tank?.oil_inches ?? tank?.current?.oil_inches ?? 0) || 0
  if (legacy > 0) {
    const totalInches = Math.max(water, legacy)
    return finalizeCuts(tank, {
      waterInches: water,
      oilInches: Math.max(0, totalInches - water),
      totalInches,
      capacityBbl: cap,
      spillInches: spill,
    })
  }

  return finalizeCuts(tank, { waterInches: water, oilInches: 0, totalInches: water, capacityBbl: cap, spillInches: spill })
}

export function fillRatio(tank: any) {
  const cap = Number(tank?.capacityBbl ?? tank?.capacity_bbl ?? CAPACITY_BBL)
  const totalBbl = Number(tank?.totalBbl ?? tank?.total_bbl ?? tank?.current?.total_bbl ?? 0)
  if (totalBbl > 0) return Math.min(1, totalBbl / cap)
  return Math.min(1, tankCuts(tank).totalInches / spillInchesFor(tank))
}

export function isGettingHigh(tank: any, status?: WatchStatus) {
  return status === 'high' || fillRatio(tank) >= 0.85
}

export function formatStrap(inches: number) {
  const safe = Math.max(0, inches)
  const feet = Math.floor(safe / 12)
  const rem = safe - feet * 12
  const whole = Math.floor(rem)
  const q = Math.round((rem - whole) * 4)
  const frac = q === 0 ? '' : q === 2 ? ' 1/2' : q === 1 ? ' 1/4' : q === 3 ? ' 3/4' : ''
  return `${feet} ft  ${whole}${frac} in`
}

export function inchesFromParts(feet: number, inches: number, quarters: number) {
  return Math.max(0, Number(feet || 0) * 12 + Number(inches || 0) + Number(quarters || 0) / 4)
}

export function commanderBayIndex(tank: any) {
  const named = String(tank?.name ?? '').match(/·\s*([1-4])\s*$/)
  if (named) return Number(named[1]) - 1
  return Number(tank?.sortOrder ?? tank?.sort_order ?? 0) % 4
}

export function commanderUnitName(tank: any) {
  return String(tank?.name ?? 'Sand Commander').replace(/\s*·\s*[1-4]\s*$/, '').trim() || 'Sand Commander'
}

export function commanderBayBbl(inches: number) {
  return COMMANDER_BBL_PER_INCH * Math.max(0, Number(inches) || 0)
}

export function commanderCutsForBay(bay: any, index = commanderBayIndex(bay)) {
  const named = { ...bay, tank_type: bay?.tank_type || bay?.tankType || 'sand-commander', name: bay?.name || `· ${index + 1}` }
  return tankCuts(named)
}

export function commanderLastBay(bays: any[]) {
  const list = [...(bays || [])].sort((a, b) => commanderBayIndex(a) - commanderBayIndex(b) || Number(a.id) - Number(b.id))
  return list.find((bay) => commanderBayIndex(bay) === 3) || list[list.length - 1] || null
}

export function commanderEqualized(bays: any[]) {
  const last = commanderLastBay(bays)
  if (!last) return false
  return commanderCutsForBay(last, 3).totalInches >= COMMANDER_WEIR_INCHES - 0.05
}

export function commanderVisualBays(unit: { bays?: any[] }) {
  const raw = [...(unit?.bays || [])].sort((a, b) => commanderBayIndex(a) - commanderBayIndex(b) || Number(a.id) - Number(b.id))
  const filled = (seed: any) => ({
    waterInches: COMMANDER_WEIR_INCHES,
    oilInches: 0,
    water_inches: COMMANDER_WEIR_INCHES,
    oil_column_inches: 0,
    fluid_label: seed?.fluid_label || seed?.fluidLabel || 'Fresh',
    tank_type: 'sand-commander',
    virtual: true,
  })
  let list = raw
  if (raw.length === 1) {
    list = [filled(raw[0]), filled(raw[0]), filled(raw[0]), raw[0]]
  }
  while (list.length < 4) list.push({})
  const mapped = list.slice(0, 4).map((bay, i) => {
    const cuts = commanderCutsForBay(bay, i)
    return {
      ...bay,
      name: COMMANDER_LABELS[i],
      index: i,
      waterInches: cuts.waterInches,
      oilInches: cuts.oilInches,
      totalInches: cuts.totalInches,
      spillInches: i >= 3 ? COMMANDER_FULL_INCHES : COMMANDER_WEIR_INCHES,
      strap: i === 3,
      virtual: !!bay.virtual || !bay.id,
    }
  })
  const last = mapped[3]
  if ((last?.totalInches || 0) >= COMMANDER_WEIR_INCHES - 0.05) {
    return mapped.map((bay, i) => ({
      ...bay,
      waterInches: last.waterInches,
      oilInches: last.oilInches,
      totalInches: last.totalInches,
      spillInches: COMMANDER_FULL_INCHES,
      equalized: true,
      strap: i === 3,
    }))
  }
  return mapped.map((bay, i) => {
    if (i >= 3 || bay.totalInches <= COMMANDER_WEIR_INCHES + 0.05) return bay
    const water = Math.min(bay.waterInches, COMMANDER_WEIR_INCHES)
    return {
      ...bay,
      waterInches: water,
      oilInches: Math.max(0, COMMANDER_WEIR_INCHES - water),
      totalInches: COMMANDER_WEIR_INCHES,
      spillInches: COMMANDER_WEIR_INCHES,
    }
  })
}

export function commanderBackReady(bays: any[]) {
  return [0, 1, 2].every((i) => commanderCutsForBay(bays?.[i], i).totalInches >= COMMANDER_WEIR_INCHES - 0.5)
}

export function commanderFlowCopy(bays: any[]) {
  if (!commanderBackReady(bays)) {
    return 'Fill the back three to the 48 in weir before the job starts. Last bay stays empty and pumps through the filter pods into the 400 upright.'
  }
  if (commanderEqualized(bays)) {
    return 'Equalized — last bay hit 48 in. All four bays are one tank and will fill together. Pump the last bay down through the pods and keep it below 48 in.'
  }
  return 'Last bay pumps through the filter pods into the 400 upright. Keep it below 48 in. At 48 in the weirs lock and the whole tank fills as one.'
}

export function commanderUnitBarrels(bays: any[]) {
  return commanderVisualBays({ bays }).reduce((sum, bay) => ({
    oil: sum.oil + commanderBayBbl(bay.oilInches),
    water: sum.water + commanderBayBbl(bay.waterInches),
    fluid: sum.fluid + commanderBayBbl(bay.totalInches),
  }), { oil: 0, water: 0, fluid: 0 })
}

export function commanderPrimary(unit: { bays: any[] }) {
  const bays = unit?.bays || []
  return bays.find((bay) => commanderBayIndex(bay) === 3) || bays[bays.length - 1] || bays[0]
}

export function groupCommanderUnits(tanks: any[]) {
  const map = new Map<string, any[]>()
  for (const tank of tanks) {
    const key = String(tank?.tankGroup ?? tank?.tank_group ?? `solo-${tank?.id}`)
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(tank)
  }
  return [...map.values()].map((bays) => {
    const sorted = [...bays].sort((a, b) => commanderBayIndex(a) - commanderBayIndex(b) || Number(a.id) - Number(b.id))
    return {
      id: sorted[0]?.tankGroup || sorted[0]?.tank_group || sorted[0]?.id,
      name: commanderUnitName(sorted[0]),
      bays: sorted,
    }
  })
}

export function partsFromInches(total: number) {
  const safe = Math.max(0, total)
  const feet = Math.floor(safe / 12)
  const rem = safe - feet * 12
  const inches = Math.floor(rem)
  const quarters = Math.min(3, Math.max(0, Math.round((rem - inches) * 4)))
  return { feet, inches, quarters }
}
